import React, { useEffect, useState } from 'react';
import { Grid, TextField } from '@material-ui/core';

import { useTranslation } from 'react-i18next';

import { useDispatch } from 'react-redux';
import { sawmillActions } from 'actions/sawmillActions';
import { AxiosError } from 'axios';
import { notificationActions } from 'actions/notificationActions';
import { CreateEditDialog } from './CreateEditDialog';
import { ValidatedTextInput } from 'components/ValidatedTextInput';
import { SawmillUserResponse } from '.generated/api';

type CreateEditSawmillUserDialogProps = {
  isOpen: boolean;
  handleClose: () => void;
  sawmillId: string;
  sawmillUser?: SawmillUserResponse;
};

const CreateEditSawmillUserDialog: React.FC<
  CreateEditSawmillUserDialogProps
> = ({ isOpen, handleClose, sawmillId, sawmillUser }) => {
  const { t } = useTranslation(['translations']);
  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isInputValid, setInputValid] = useState<boolean>(true);
  const [isEmailValid, setEmailValid] = useState<boolean>(true);
  const [newSawmillUser, setNewSawmillUser] = useState<{
    name: string;
    email: string;
    phone: string;
  }>({
    name: '',
    email: '',
    phone: '',
  });

  const handleEmailChange = (name: string, value: string, error: boolean) => {
    setEmailValid(!error);
    setNewSawmillUser((newSawmillUser) => ({
      ...newSawmillUser,
      [name]: value,
    }));
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { value, name } = e.target;
    setNewSawmillUser((newSawmillUser) => ({
      ...newSawmillUser,
      [name]: value,
    }));
  };

  const createEditCallback = (error?: AxiosError) => {
    setIsLoading(false);
    if (error) {
      dispatch(notificationActions.enqueueErrorResponse(error));
    } else {
      handleCloseDialog();
    }
  };

  const handleCloseDialog = () => {
    setNewSawmillUser({
      name: '',
      email: '',
      phone: '',
    });
    handleClose();
  };

  const handleSubmit = () => {
    setIsLoading(true);
    if (sawmillUser) {
      dispatch(
        sawmillActions.updateSawmillUser(
          sawmillId,
          sawmillUser.email,
          newSawmillUser.name.length > 0 ? newSawmillUser.name : undefined,
          newSawmillUser.phone.length > 0 ? newSawmillUser.phone : undefined,
          createEditCallback,
        ),
      );
    } else {
      dispatch(
        sawmillActions.addUserToSawmill(
          sawmillId,
          newSawmillUser.email,
          newSawmillUser.name.length > 0 ? newSawmillUser.name : undefined,
          newSawmillUser.phone.length > 0 ? newSawmillUser.phone : undefined,
          createEditCallback,
        ),
      );
    }
  };

  // validate
  useEffect(() => {
    setInputValid(newSawmillUser.email.length > 0 && isEmailValid);
  }, [newSawmillUser]);

  // initialize
  useEffect(() => {
    if (sawmillUser) {
      setNewSawmillUser({
        email: sawmillUser.email,
        name: sawmillUser.name ?? '',
        phone: sawmillUser.phone ?? '',
      });
    } else {
      setNewSawmillUser({
        name: '',
        email: '',
        phone: '',
      });
    }
  }, [sawmillUser]);

  return (
    <CreateEditDialog
      title={
        sawmillUser
          ? t('organization_page.sawmills.dialog.edit_sawmill_user')
          : t('organization_page.sawmills.dialog.create_sawmill_user')
      }
      isOpen={isOpen}
      isLoading={isLoading}
      editMode={!!sawmillUser}
      canSubmit={isInputValid}
      onSubmit={handleSubmit}
      onClose={handleCloseDialog}
    >
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            autoFocus
            type="text"
            variant="outlined"
            margin="none"
            fullWidth
            label={t('organization_page.sawmills.labels.sawmill_name')}
            name="name"
            value={newSawmillUser.name}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            type="text"
            variant="outlined"
            margin="none"
            fullWidth
            label={t('organization_page.contacts.labels.contact_phone')}
            name="phone"
            value={newSawmillUser.phone}
            onChange={handleChange}
          />
        </Grid>
        {!sawmillUser && (
          <Grid item xs={12}>
            <ValidatedTextInput
              type="email"
              required={false}
              label={t('organization_page.contacts.labels.contact_email')}
              handleChangeCallback={handleEmailChange}
              inputName="email"
              defaultValue={newSawmillUser.email}
              autoFocus={false}
              margin="none"
            />
          </Grid>
        )}
      </Grid>
    </CreateEditDialog>
  );
};

export { CreateEditSawmillUserDialog };
